import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Copy, ExternalLink, Check } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const ELLA_DONATE = "https://customer-assets.emergentagent.com/job_run-for-ella/artifacts/57q9tq5d_a1afcd51-6c09-4419-b7f1-8deacb19c643.JPG";

const bankAccounts = [
  {
    name: "Raiffeisenbank im Oberpfälzer Jura",
    iban: "DE96 7506 9061 0004 2132 38",
  },
  {
    name: "Sparkasse Parsberg",
    iban: "DE52 7605 2080 0042 8860 28",
  },
];

function IBANCopyBox({ bank }) {
  const [copied, setCopied] = useState(false);
  const copyIBAN = () => {
    navigator.clipboard.writeText(bank.iban.replace(/\s/g, ""));
    setCopied(true);
    toast.success("IBAN kopiert!");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      data-testid={`iban-copy-${bank.iban.slice(-4)}`}
      onClick={copyIBAN}
      className="iban-box w-full text-left group"
    >
      <p className="text-base md:text-lg font-semibold mb-2 text-white">{bank.name}</p>
      <div className="flex items-center justify-between gap-3">
        <p className="font-heading text-lg md:text-2xl font-bold tracking-wider text-white">
          {bank.iban}
        </p>
        {copied ? (
          <Check className="w-5 h-5 shrink-0" style={{ color: "#10B981" }} />
        ) : (
          <Copy className="w-5 h-5 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity text-white" />
        )}
      </div>
    </button>
  );
}

export default function DonationInfo() {
  const [leftRef, leftVisible] = useScrollAnimation({ threshold: 0.15 });
  const [rightRef, rightVisible] = useScrollAnimation({ threshold: 0.15 });

  return (
    <section id="spenden" data-testid="donation-section" className="py-20 md:py-28 relative overflow-hidden" style={{ background: "#b46eb6" }}>
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full opacity-10" style={{ background: "#7DD4B5", filter: "blur(100px)" }} />
      <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full opacity-10" style={{ background: "#ffffff", filter: "blur(80px)" }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Info */}
          <div 
            ref={leftRef}
            className={`space-y-6 text-left animate-fade-left ${leftVisible ? 'visible' : ''}`}
          >
            <div>
              <p className="font-accent text-xl md:text-2xl mb-2" style={{ color: "#E8DAEF" }}>
                Jede Spende zählt
              </p>
              <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-white">
                Spendeninformationen
              </h2>
              <div className="h-1 w-16 rounded-full mt-4" style={{ background: "rgba(149, 225, 187, 0.5)" }} />
            </div>

            <p className="text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.85)" }}>
              Empfänger: <strong className="text-white">Zukunft durch medizinische Forschung e.V.</strong>
            </p>

            <div className="space-y-4">
              {bankAccounts.map((bank, idx) => (
                <IBANCopyBox key={idx} bank={bank} />
              ))}
            </div>

            {/* GoFundMe */}
            <Button
              data-testid="gofundme-btn"
              asChild
              className="rounded-full px-8 py-6 text-base font-bold bg-white hover:bg-gray-50 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1"
              style={{ color: "#b46eb6" }}
            >
              <a
                href="https://www.gofundme.com/f/jeder-schritt-zahlt-fur-eine-zukunft-ohne-rettsyndrom"
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink className="w-5 h-5 mr-2" />
                Auf GoFundMe spenden
              </a>
            </Button>
          </div>

          {/* Right: Photo */}
          <div 
            ref={rightRef}
            className={`hidden lg:block animate-fade-right ${rightVisible ? 'visible' : ''}`}
          >
            <img
              data-testid="donation-ella-photo"
              src={ELLA_DONATE}
              alt="Ella lacht"
              className="w-full max-w-sm mx-auto rounded-3xl shadow-2xl object-cover"
              style={{ aspectRatio: "3/4" }}
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
